import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { encodeAgentMentionLabel } from '@crowdex/ui/lib/inline-reference';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { type PluginPanel, railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { dockApp } from '@/lib/desktop-shell/app-panels';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { dockArtifact } from '@/lib/desktop-shell/open-artifact';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { useLibraryPage } from '@/lib/navigation';
import { artifactMentionTarget } from '@/lib/side-panel/mention-targets';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';
import { navigateApp } from '@/vite/navigation-store';
import {
  askNextConversation,
  draftInConversation,
  draftNextConversation,
  insertNextConversation,
  sendInConversation,
} from './agent-ask';
import { refreshAppSidebar } from './app-panel-sidebar';
import { callApp } from './apps-client';
import { listAppArtifacts } from './artifacts-client';
import { ensureConversationSaved } from './use-app-conversation';
import { refreshArtifactIndex } from './use-artifact-index';

/**
 * What surfaces outside a conversation (an app's home, the Library) and an artifact's tab ask the
 * shell: a conversation started with an app docked, an artifact opened where it was made, a copy
 * of an artifact, a change asked about one. The shell runs them (`AppConversationHost`); without
 * it (no shell mounted) they reject.
 */
const SHELL_REQUEST = 'crowdex:app-conversation-request';

type ShellRequest =
  | {
      kind: 'start';
      /** The app docked in the new conversation, if any. */
      view: SidePanelView | null;
      /** What waits in its composer: an artifact referenced, an example's prompt. */
      artifact?: ArtifactListingEntry;
      prompt?: string;
      /** What it sends as soon as its composer is there. */
      send?: string;
    }
  | { kind: 'open'; artifact: Pick<ArtifactListingEntry, 'id' | 'conversation'> }
  | { kind: 'show'; conversation: string }
  | { kind: 'duplicate'; artifact: ArtifactListingEntry; into: 'shown' | 'new' }
  | {
      kind: 'ask';
      artifact: ArtifactListingEntry;
      text: string;
      /** The conversation the request goes to; `null`, a new one. */
      conversation: string | null;
    };

type Answer<R extends ShellRequest> = R extends { kind: 'open' } | { kind: 'show' }
  ? boolean
  : R extends { kind: 'duplicate' }
    ? ArtifactListingEntry | null
    : undefined;

/** Hands a request to the shell's host; false when no shell is mounted to run it. */
function dispatch(
  detail: ShellRequest,
  settle: (error: unknown, value?: unknown) => void,
): boolean {
  return !window.dispatchEvent(
    new CustomEvent(SHELL_REQUEST, { detail: { ...detail, settle }, cancelable: true }),
  );
}

function request<R extends ShellRequest>(detail: R): Promise<Answer<R>> {
  return new Promise((resolve, reject) => {
    const delivered = dispatch(detail, (error, value) =>
      error === undefined ? resolve(value as Answer<R>) : reject(error),
    );
    if (!delivered) reject(new Error('Conversations are not available here.'));
  });
}

/**
 * "New …" of an app's home (and its example prompts): a new conversation with the app docked in
 * its side panel, a prompt or an artifact waiting in its composer for the user to complete, or a
 * first message sent (`send`).
 */
export function startAppConversation(
  view: SidePanelView | null,
  options: {
    prompt?: string;
    artifact?: ArtifactListingEntry;
    /** Its first message, sent as soon as its composer is there (a guided creation). */
    send?: string;
  } = {},
): Promise<void> {
  return request({ kind: 'start', view, ...options });
}

/**
 * A new conversation that sends `text` once its composer is there ("New persona", a plugin
 * written with the agent, Dictate with no composer in view). With no shell mounted, the app opens
 * on a new conversation (`newSession`), which takes the request.
 */
export function openNewConversation(text?: string): void {
  const delivered = dispatch(
    { kind: 'start', view: null, ...(text === undefined ? {} : { send: text }) },
    () => undefined,
  );
  if (delivered) return;
  if (text !== undefined) askNextConversation(text);
  navigateApp('/desktop?newSession=true');
}

/** Shows a conversation (an assistant's channel) from a page outside one; false when it cannot. */
export function showConversation(conversation: string): Promise<boolean> {
  return request({ kind: 'show', conversation }).catch(() => false);
}

/**
 * Opens an artifact in the conversation that made it, docked in its side panel. False when it has
 * none, or the shell cannot show that conversation.
 */
export function openArtifactInConversation(
  artifact: Pick<ArtifactListingEntry, 'id' | 'conversation'>,
): Promise<boolean> {
  if (artifact.conversation === null) return Promise.resolve(false);
  return request({ kind: 'open', artifact }).catch(() => false);
}

/**
 * Duplicate: a copy of the artifact (its type's `duplicate` route) in the conversation on screen
 * (`shown`, saved first if new) or in a new one with the app docked (`new`, from outside a
 * conversation), opened there on the copy, editable, the composer focused on it. Plugin artifacts
 * are read-only: this is how one becomes the user's. Resolves with the copy, as the index lists it.
 */
export function duplicateArtifact(
  artifact: ArtifactListingEntry,
  into: 'shown' | 'new',
): Promise<ArtifactListingEntry | null> {
  return request({ kind: 'duplicate', artifact, into });
}

/**
 * A change asked about an artifact from its page (the item page's composer): to `conversation`
 * (by default the one that made it) or a new one. That conversation opens with the item docked
 * and gets the request with the item referenced. A plugin's artifact is read-only: it is
 * duplicated there first, and the request is about the copy.
 */
export function askAboutArtifact(
  artifact: ArtifactListingEntry,
  text: string,
  conversation: string | null,
): Promise<void> {
  return request({ kind: 'ask', artifact, text, conversation });
}

/** An artifact as the composer sends it: `@[title](artifact:<id>)`. */
function artifactReference(artifact: ArtifactListingEntry): string {
  const target = artifactMentionTarget(artifact);
  return `@[${encodeAgentMentionLabel(target.label)}](artifact:${target.id})`;
}

/** Whether an artifact is a plugin's (a template, a design system): read-only, "From <App>". */
export function readOnlyArtifact(artifact: Pick<ArtifactListingEntry, 'space'>): boolean {
  return artifact.space === 'plugin';
}

/**
 * What a type's `duplicate` route answers: the copy (in the conversation's folder unless it says
 * another space, the Drive for what is the user's), and its item in the app's sidebar.
 */
const duplicatedSchema = z.object({
  space: z.enum(['workspace', 'drive']).optional(),
  path: z.string().min(1),
  item: z.string().min(1),
});

/** The app (with a view) whose type the artifact is. */
export function appPanelOf(
  panels: readonly PluginPanel[],
  plugin: string,
): (PluginPanel & { appView: NonNullable<PluginPanel['appView']> }) | undefined {
  return panels.find(
    (panel): panel is PluginPanel & { appView: NonNullable<PluginPanel['appView']> } =>
      panel.appView?.plugin === plugin,
  );
}

/**
 * Copies `artifact` into `conversation`'s workspace (saved first when new) and docks its app on
 * the copy there. Returns the copy, as the index lists it once it does.
 */
async function duplicateInto(
  store: DesktopShellStore,
  panels: readonly PluginPanel[],
  artifact: ArtifactListingEntry,
  conversation: string,
  title: string,
): Promise<ArtifactListingEntry | null> {
  const panel = appPanelOf(panels, artifact.plugin);
  const route = artifact.duplicate;
  if (route === null || panel === undefined)
    throw new Error(`${artifact.title} cannot be duplicated.`);
  await ensureConversationSaved(store, conversation, title);
  const copy = duplicatedSchema.parse(
    await callApp({
      plugin: artifact.plugin,
      route,
      method: 'POST',
      input: { space: artifact.space, path: artifact.path },
      conversation,
    }),
  );
  // Opened on the copy, in the app's tab of that conversation (if it is still the one shown).
  if (store.getState().chatId === conversation)
    dockApp(store, panel.view, { item: copy.item, section: panel.rail?.sidebars?.[0]?.id });
  refreshAppSidebar(panel.view);
  void refreshArtifactIndex();
  const listed = await listAppArtifacts(conversation).catch(() => []);
  return (
    listed.find(
      (entry) =>
        entry.plugin === artifact.plugin &&
        entry.space === (copy.space ?? 'workspace') &&
        entry.path === copy.path,
    ) ?? null
  );
}

/**
 * Runs the requests above in the shell: `newTask` starts a conversation (its New conversation),
 * after leaving a library page (an app's home, the Library) so that the conversation shows.
 */
export function AppConversationHost({ newTask }: { newTask: () => void }) {
  const store = useDesktopShellStoreApi();
  const opener = useConversationOpener();
  const workbench = useWorkbench();
  const library = useLibraryPage();
  const latest = useRef({ leave: library.leave, newTask, workbench });
  latest.current = { leave: library.leave, newTask, workbench };
  useEffect(() => {
    if (opener === null) return;
    const panels = () => railPanels(latest.current.workbench);
    const titleOf = (plugin: string) =>
      latest.current.workbench.pluginDisplayNames[plugin] ??
      appPanelOf(panels(), plugin)?.title ??
      'App';
    /** A new conversation on screen (a library page left first). Returns its id. */
    const startNew = (view: SidePanelView | null): string => {
      latest.current.leave();
      latest.current.newTask();
      if (view !== null) dockApp(store, view);
      return store.getState().chatId;
    };
    /** Shows `conversation` (a library page left first); false when it cannot. */
    const show = async (conversation: string): Promise<boolean> => {
      if (store.getState().chatId === conversation) {
        latest.current.leave();
        return true;
      }
      const shown = await opener.openConversation(conversation);
      if (shown) latest.current.leave();
      return shown;
    };
    const run = async (detail: ShellRequest): Promise<unknown> => {
      switch (detail.kind) {
        case 'start': {
          // Before the new conversation's composer mounts, never in the one open now.
          if (detail.artifact !== undefined)
            draftNextConversation(artifactMentionTarget(detail.artifact));
          if (detail.prompt !== undefined) insertNextConversation(detail.prompt);
          if (detail.send !== undefined) askNextConversation(detail.send);
          // A plain conversation: an assistant is its own conversation (docs/adr-assistants.md).
          startNew(detail.view);
          return undefined;
        }
        case 'show':
          return show(detail.conversation);
        case 'open': {
          const conversation = detail.artifact.conversation;
          if (conversation === null || !(await show(conversation))) return false;
          dockArtifact(store, detail.artifact.id);
          return true;
        }
        case 'duplicate': {
          const { artifact } = detail;
          const conversation =
            detail.into === 'new'
              ? startNew(appPanelOf(panels(), artifact.plugin)?.view ?? null)
              : store.getState().chatId;
          const copy = await duplicateInto(
            store,
            panels(),
            artifact,
            conversation,
            titleOf(artifact.plugin),
          );
          // The composer is the way to change it: focused, the copy referenced.
          if (copy !== null) draftInConversation(artifactMentionTarget(copy), conversation);
          return copy;
        }
        case 'ask': {
          const { artifact, text } = detail;
          let conversation: string;
          if (detail.conversation === null) conversation = startNew(null);
          else if (await show(detail.conversation)) conversation = detail.conversation;
          else throw new Error('That conversation could not be opened.');
          // The request is about the item docked beside it: a copy of a plugin's (read-only).
          let about: ArtifactListingEntry | null = artifact;
          if (readOnlyArtifact(artifact))
            about = await duplicateInto(
              store,
              panels(),
              artifact,
              conversation,
              titleOf(artifact.plugin),
            );
          else dockArtifact(store, artifact.id);
          sendInConversation(
            about === null ? text : `${artifactReference(about)} ${text}`,
            conversation,
          );
          return undefined;
        }
      }
    };
    const handle = (event: Event) => {
      event.preventDefault();
      const detail = (
        event as CustomEvent<ShellRequest & { settle(error?: unknown, value?: unknown): void }>
      ).detail;
      run(detail).then(
        (value) => detail.settle(undefined, value),
        (error: unknown) => detail.settle(error ?? new Error('It could not be done.')),
      );
    };
    window.addEventListener(SHELL_REQUEST, handle);
    return () => window.removeEventListener(SHELL_REQUEST, handle);
  }, [opener, store]);
  return null;
}
